<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">
              {{ $t('government.grievance_management.title') }}
            </h2>
            <p class="text-gray-600">
              {{ $t('government.grievance_management.subtitle') }}
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="px-4 py-2 bg-forest-green text-white rounded-md hover:bg-green-700 text-sm font-medium"
            >
              <i class="fa-solid fa-plus mr-2"></i>
              {{ $t('government.grievance_management.new_grievance') }}
            </button>
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Grievance Management Content -->
      <main id="grievance-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Summary Cards -->
        <div
          id="grievance-stats"
          class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        >
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.grievance_management.stats.open_grievances') }}</p>
                <p class="text-2xl font-medium text-error-red">7</p>
              </div>
              <i
                class="fa-solid fa-exclamation-circle text-error-red text-xl"
              ></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.grievance_management.stats.under_investigation') }}</p>
                <p class="text-2xl font-medium text-yellow-600">3</p>
              </div>
              <i class="fa-solid fa-search text-yellow-600 text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.grievance_management.stats.resolved_this_month') }}</p>
                <p class="text-2xl font-medium text-forest-green">12</p>
              </div>
              <i class="fa-solid fa-check-circle text-forest-green text-xl"></i>
            </div>
          </div>
          <div class="bg-white p-5 rounded-md border border-light-gray">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ $t('government.grievance_management.stats.avg_resolution_time') }}</p>
                <p class="text-2xl font-medium text-dark-navy">8.5</p>
                <p class="text-xs text-gray-500">{{ $t('government.grievance_management.stats.days') }}</p>
              </div>
              <i class="fa-solid fa-clock text-electric-blue text-xl"></i>
            </div>
          </div>
        </div>

        <!-- Filters and Search -->
        <div
          id="grievance-filters"
          class="bg-white p-5 rounded-md border border-light-gray mb-6"
        >
          <div class="flex flex-wrap items-center gap-4">
            <div class="flex-1 min-w-64">
              <div class="relative">
                <i
                  class="fa-solid fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                ></i>
                <input
                  type="text"
                  :placeholder="$t('government.grievance_management.filters.search_placeholder')"
                  class="w-full pl-10 pr-4 py-2 border border-light-gray rounded-md focus:outline-none focus:ring-2 focus:ring-forest-green"
                />
              </div>
            </div>
            <select
              class="px-3 py-2 border border-light-gray rounded-md text-sm"
            >
              <option>{{ $t('government.grievance_management.filters.all_status') }}</option>
              <option>{{ $t('government.grievance_management.filters.open') }}</option>
              <option>{{ $t('government.grievance_management.filters.under_investigation') }}</option>
              <option>{{ $t('government.grievance_management.filters.resolved') }}</option>
              <option>{{ $t('government.grievance_management.filters.closed') }}</option>
            </select>
            <select
              class="px-3 py-2 border border-light-gray rounded-md text-sm"
            >
              <option>{{ $t('government.grievance_management.filters.all_priority') }}</option>
              <option>{{ $t('government.grievance_management.filters.high') }}</option>
              <option>{{ $t('government.grievance_management.filters.medium') }}</option>
              <option>{{ $t('government.grievance_management.filters.low') }}</option>
            </select>
            <select
              class="px-3 py-2 border border-light-gray rounded-md text-sm"
            >
              <option>{{ $t('government.grievance_management.filters.all_projects') }}</option>
              <option>Solar Farm A</option>
              <option>Wind Project B</option>
              <option>Forest Conservation C</option>
            </select>
            <button
              class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
            >
              <i class="fa-solid fa-filter mr-2"></i>
              {{ $t('government.grievance_management.filters.filter') }}
            </button>
          </div>
        </div>

        <!-- Grievance List -->
        <div
          id="grievance-list"
          class="bg-white rounded-md border border-light-gray"
        >
          <div class="p-5 border-b border-light-gray">
            <div class="flex justify-between items-center">
              <h3 class="text-lg font-medium text-dark-navy">
                {{ $t('government.grievance_management.grievance_list.title') }}
              </h3>
              <div class="flex items-center space-x-2">
                <button class="p-2 rounded-md hover:bg-gray-100">
                  <i class="fa-solid fa-download text-gray-600"></i>
                </button>
                <button class="p-2 rounded-md hover:bg-gray-100">
                  <i class="fa-solid fa-print text-gray-600"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.grievance_id') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.project') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.complainant') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.category') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.priority') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.status') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.date_submitted') }}
                  </th>
                  <th class="text-left p-4 text-sm font-medium text-gray-700">
                    {{ $t('government.grievance_management.grievance_list.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-light-gray">
                <tr class="hover:bg-gray-50">
                  <td class="p-4">
                    <span class="font-medium text-dark-navy">GRV-2024-001</span>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Solar Farm Project A</p>
                      <p class="text-sm text-gray-600">PIN-2024-001</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">John Mukamuri</p>
                      <p class="text-sm text-gray-600">{{ $t('government.grievance_management.grievance_list.roles.community_rep') }}</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.grievance_list.categories.environmental') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.high') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.under_investigation') }}</span
                    >
                  </td>
                  <td class="p-4 text-sm text-gray-600">2024-01-15</td>
                  <td class="p-4">
                    <div class="flex space-x-2">
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="View Details"
                      >
                        <i class="fa-solid fa-eye text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <i class="fa-solid fa-edit text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Add Note"
                      >
                        <i class="fa-solid fa-comment text-gray-600"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="p-4">
                    <span class="font-medium text-dark-navy">GRV-2024-002</span>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Wind Project B</p>
                      <p class="text-sm text-gray-600">PIN-2024-002</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Mary Chikwanha</p>
                      <p class="text-sm text-gray-600">{{ $t('government.grievance_management.grievance_list.roles.local_farmer') }}</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.grievance_list.categories.social_impact') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.medium') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.open') }}</span
                    >
                  </td>
                  <td class="p-4 text-sm text-gray-600">2024-01-18</td>
                  <td class="p-4">
                    <div class="flex space-x-2">
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="View Details"
                      >
                        <i class="fa-solid fa-eye text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <i class="fa-solid fa-edit text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Add Note"
                      >
                        <i class="fa-solid fa-comment text-gray-600"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="p-4">
                    <span class="font-medium text-dark-navy">GRV-2024-003</span>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Forest Conservation C</p>
                      <p class="text-sm text-gray-600">PIN-2024-003</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Peter Zimba</p>
                      <p class="text-sm text-gray-600">{{ $t('government.grievance_management.grievance_list.roles.traditional_leader') }}</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.grievance_list.categories.economic') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.high') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.under_investigation') }}</span
                    >
                  </td>
                  <td class="p-4 text-sm text-gray-600">2024-01-20</td>
                  <td class="p-4">
                    <div class="flex space-x-2">
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="View Details"
                      >
                        <i class="fa-solid fa-eye text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <i class="fa-solid fa-edit text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Add Note"
                      >
                        <i class="fa-solid fa-comment text-gray-600"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr class="hover:bg-gray-50">
                  <td class="p-4">
                    <span class="font-medium text-dark-navy">GRV-2024-004</span>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Biogas Project D</p>
                      <p class="text-sm text-gray-600">PIN-2024-004</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <div>
                      <p class="font-medium">Grace Moyo</p>
                      <p class="text-sm text-gray-600">{{ $t('government.grievance_management.grievance_list.roles.ngo_representative') }}</p>
                    </div>
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.grievance_list.categories.procedural') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.low') }}</span
                    >
                  </td>
                  <td class="p-4">
                    <span
                      class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
                      >{{ $t('government.grievance_management.filters.open') }}</span
                    >
                  </td>
                  <td class="p-4 text-sm text-gray-600">2024-01-22</td>
                  <td class="p-4">
                    <div class="flex space-x-2">
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="View Details"
                      >
                        <i class="fa-solid fa-eye text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <i class="fa-solid fa-edit text-gray-600"></i>
                      </button>
                      <button
                        class="p-1 rounded hover:bg-gray-100"
                        title="Add Note"
                      >
                        <i class="fa-solid fa-comment text-gray-600"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="p-4 border-t border-light-gray">
            <div class="flex justify-between items-center">
              <p class="text-sm text-gray-600">{{ $t('government.grievance_management.pagination.showing') }} 1-4 {{ $t('government.grievance_management.pagination.of') }} 7 {{ $t('government.grievance_management.pagination.grievances') }}</p>
              <div class="flex space-x-2">
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.grievance_management.pagination.previous') }}
                </button>
                <button
                  class="px-3 py-1 bg-forest-green text-white rounded-md text-sm"
                >
                  1
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  2
                </button>
                <button
                  class="px-3 py-1 border border-light-gray rounded-md text-sm hover:bg-gray-50"
                >
                  {{ $t('government.grievance_management.pagination.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>
